import React, { useCallback, useState, useMemo } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import { yupResolver } from '@hookform/resolvers/yup'
import toast from 'react-hot-toast'
import AnswerLayout, { AnswerMain, AnswerSidebar } from './AnswerLayout'

import PageLoader from 'Layout/PageLoader'

import Tags from '../../../../components/Tags'

import AnswerForm from './AnswerForm'
import answerFormValidationSchema from './AnswerForm/answer-form-validation-schema'
import { useGetAnswerByIdQuery, useUpdateAnswerMutation, useDeleteAnswerMutation } from './answers.service'
import parseErrorMessage from 'Utils/error-message-parser'
import SwitchInput from 'Design/components/SwitchInput'
import ConfirmationModal from 'Design/components/ConfirmationModal'

const AnswerFormHandler = (props) => {
  const { answer } = props
  const navigate = useNavigate()

  const formMethods = useForm({
    defaultValues: {
      name: answer.name,
      content: answer.answer
    },
    resolver: yupResolver(answerFormValidationSchema)
  })

  // const { watch, getValues, setValue } = formMethods
  const [isAnswerActive, setIsAnswerActive] = useState(answer.is_active)
  const [updateAnswer, answerResult] = useUpdateAnswerMutation()
  const [deleteAnswer, deleteAnswerResult] = useDeleteAnswerMutation()
  const [tags, setTags] = useState(answer.tags ?? [])
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showUnPublishedModal, setShowUnPublishedModal] = useState(false)

  const tagsValue = useMemo(() => {
    return tags.map((tag) => {
      return {
        label: tag,
        value: tag
      }
    })
  }, [tags])

  const update = useCallback(
    (payload) => {
      const promise = updateAnswer({ id: answer.id, ...payload }).unwrap()
      toast.promise(promise, {
        loading: 'Updating answer...',
        success: 'Answer updated successfully.',
        error: parseErrorMessage('Unable to update answer. Try again...')
      })
      return promise
    },
    [updateAnswer]
  )

  const onSubmit = useCallback(
    async (data) => {
      const payload = {
        id: answer.id,
        name: data.name,
        content: data.content
      }

      update(payload)
    },
    [answer.id, update]
  )

  const onDeleteAnswer = useCallback(() => {
    const promise = deleteAnswer(answer.id).unwrap()

    toast.promise(promise, {
      loading: 'Deleting answer...',
      success: 'Answer deleted successfully.',
      error: parseErrorMessage('Unable to delete answer. Try again...')
    })

    promise.then(() => {
      navigate('../')
    })
  }, [answer.id, deleteAnswer, navigate])

  const handleActiveAnswerStateChange = (e) => {
    const isActive = e.target.checked

    if (isActive) {
      setIsAnswerActive(isActive)
      update({ is_active: isActive }).catch(() => setIsAnswerActive(!isActive))
    } else {
      setShowUnPublishedModal(true)
    }
  }

  const handlePublishedStateChange = (e) => {
    setIsAnswerActive(false)
    update({ is_active: false }).catch(() => setIsAnswerActive(true))
    setShowUnPublishedModal(false)
  }

  const handleTagCreate = (tag) => {
    setTags((previousTags) => {
      const newTags = [...previousTags, tag.value]
      update({ tags: newTags })
      return newTags
    })
  }

  const handleTagRemove = (tag) => {
    setTags((previousTags) => {
      const newTags = previousTags.filter((t) => t !== tag.value)
      update({ tags: newTags })
      return newTags
    })
  }

  const handleOpenDeleteModal = () => {
    setShowDeleteModal(true)
  }

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false)
  }

  const handleCloseUnPublishedModal = () => {
    setShowUnPublishedModal(false)
  }

  const isLoading = answerResult.isLoading || deleteAnswerResult.isLoading
  const isUpdating = answerResult.isLoading

  return (
    <AnswerLayout>
      <ConfirmationModal
        isOpen={showDeleteModal}
        title='Delete Answer'
        description='Are you sure to delete this answer?'
        onCloseButtonClick={handleCloseDeleteModal}
        onConfirmButtonClick={onDeleteAnswer}
        confirmButtonText='Yes'
        cancelButtonText='No'
        disableConfirmButton={isLoading}
      />
      <ConfirmationModal
        isOpen={showUnPublishedModal}
        title='Unpublish Answer'
        onCloseButtonClick={handleCloseUnPublishedModal}
        onConfirmButtonClick={handlePublishedStateChange}
        confirmButtonText='Yes'
        cancelButtonText='No'
        disableConfirmButton={isUpdating}
      >
        Are you sure you want to unpublish the answer <strong>{answer?.name}</strong>? <br />
        This action will remove the answer from all connected steps.
      </ConfirmationModal>
      <AnswerMain>
        <FormProvider {...formMethods}>
          <AnswerForm onSubmit={onSubmit} onDeleteAnswer={handleOpenDeleteModal} isLoading={isLoading} />
        </FormProvider>
      </AnswerMain>
      <AnswerSidebar>
        <SwitchInput checked={isAnswerActive} onChange={handleActiveAnswerStateChange}>
          Published
        </SwitchInput>
        <Tags
          value={tagsValue}
          creatable
          onCreate={handleTagCreate}
          onRemove={handleTagRemove}
          isDisabled={isUpdating}
          isLoading={isUpdating}
        />
      </AnswerSidebar>
    </AnswerLayout>
  )
}

const EditAnswer = () => {
  const { id } = useParams()
  const { isLoading, data: answer } = useGetAnswerByIdQuery(id)

  if (isLoading) {
    return <PageLoader />
  }

  return <AnswerFormHandler answer={answer} />
}

export default EditAnswer
